import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Clock, Users, Briefcase, ArrowRight, BadgeCheck } from 'lucide-react';
import { useI18n } from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import AddressAutocomplete from './AddressAutocomplete';
import RouteMap from './RouteMap';
import { services } from '../mock';
import { estimatePrice, ZONE_LABELS } from '../pricing';

const BookingWidget = ({ variant = 'hero' }) => {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState('oneway');
  const [form, setForm] = useState({
    from: '', to: '', date: '', time: '', pax: '1', luggage: '1', vehicle: services[0].id, duration: '3'
  });
  const [fromAddr, setFromAddr] = useState(null);
  const [toAddr, setToAddr] = useState(null);

  const onChange = (k, v) => setForm({ ...form, [k]: v });

  const currentVehicle = useMemo(
    () => services.find((s) => s.id === form.vehicle) || services[0],
    [form.vehicle]
  );

  const paxOptions = useMemo(() => {
    const max = currentVehicle.maxPax || 60;
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [currentVehicle]);

  const luggageOptions = useMemo(() => {
    const max = currentVehicle.maxLuggage || 60;
    return Array.from({ length: max + 1 }, (_, i) => i);
  }, [currentVehicle]);

  // Clamp pax/luggage if user changed vehicle and current value exceeds new max
  React.useEffect(() => {
    const updates = {};
    if (parseInt(form.pax, 10) > (currentVehicle.maxPax || 60)) {
      updates.pax = String(currentVehicle.maxPax);
    }
    if (parseInt(form.luggage, 10) > (currentVehicle.maxLuggage || 60)) {
      updates.luggage = String(currentVehicle.maxLuggage);
    }
    if (Object.keys(updates).length) setForm((f) => ({ ...f, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.vehicle]);

  // Estimation
  const estimate = useMemo(() => {
    if (tab !== 'oneway') return { kind: 'quote', reason: 'hourly' };
    return estimatePrice({ vehicleId: form.vehicle, fromAddr, toAddr });
  }, [form.vehicle, fromAddr, toAddr, tab]);

  const onSubmit = (e) => {
    e.preventDefault();
    const draft = {
      ...form,
      tab,
      estimate: estimate.kind === 'flat' || estimate.kind === 'distance'
        ? `${estimate.price}€${estimate.km ? ` (~${estimate.km} km)` : ''}${estimate.kind === 'flat' ? ' [forfait]' : ''}`
        : 'Devis sur demande'
    };
    try { localStorage.setItem('ha_booking_draft', JSON.stringify(draft)); } catch (_) {}
    navigate('/contact');
  };

  const isHero = variant === 'hero';
  const cardCls = isHero ? 'bg-white text-black shadow-2xl' : 'bg-white text-black border border-black/10';

  return (
    <div className={`w-full ${cardCls}`}>
      <div className="flex border-b border-black/10">
        {[
          { k: 'oneway', label: t('book.oneway') },
          { k: 'hourly', label: t('book.hourly') }
        ].map(o => (
          <button
            key={o.k}
            type="button"
            onClick={() => setTab(o.k)}
            className={`flex-1 py-4 text-sm tracking-wide transition-colors ${tab === o.k ? 'bg-black text-white' : 'text-black/70 hover:text-black'}`}
          >
            {o.label}
          </button>
        ))}
      </div>

      <form onSubmit={onSubmit} className="p-5 md:p-6 space-y-4">
        <Field icon={<MapPin className="w-4 h-4" />} label={t('book.from')}>
          <AddressAutocomplete
            value={form.from}
            onChange={(v, addr) => { onChange('from', v); setFromAddr(addr || null); }}
            placeholder="Aéroport CDG, Roissy…"
            required
          />
        </Field>
        {tab === 'oneway' ? (
          <Field icon={<MapPin className="w-4 h-4" />} label={t('book.to')}>
            <AddressAutocomplete
              value={form.to}
              onChange={(v, addr) => { onChange('to', v); setToAddr(addr || null); }}
              placeholder="Paris, Disneyland, gare…"
              required
            />
          </Field>
        ) : (
          <Field icon={<Clock className="w-4 h-4" />} label={t('book.duration')}>
            <select value={form.duration} onChange={(e) => onChange('duration', e.target.value)} className="w-full bg-transparent outline-none text-base">
              {[2,3,4,5,6,8,10,12].map(h => <option key={h} value={h}>{h}h</option>)}
            </select>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field icon={<Calendar className="w-4 h-4" />} label={t('book.date')}>
            <Input type="date" value={form.date} onChange={(e) => onChange('date', e.target.value)} required className="border-0 px-0 focus-visible:ring-0 text-base" />
          </Field>
          <Field icon={<Clock className="w-4 h-4" />} label={t('book.time')}>
            <Input type="time" value={form.time} onChange={(e) => onChange('time', e.target.value)} required className="border-0 px-0 focus-visible:ring-0 text-base" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field icon={<Users className="w-4 h-4" />} label={t('book.pax')}>
            <select value={form.pax} onChange={(e) => onChange('pax', e.target.value)} className="w-full bg-transparent outline-none text-base">
              {paxOptions.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field icon={<Briefcase className="w-4 h-4" />} label={t('book.luggage')}>
            <select value={form.luggage} onChange={(e) => onChange('luggage', e.target.value)} className="w-full bg-transparent outline-none text-base">
              {luggageOptions.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
        </div>
        <Field icon={<ArrowRight className="w-4 h-4" />} label={t('book.vehicle')}>
          <select value={form.vehicle} onChange={(e) => onChange('vehicle', e.target.value)} className="w-full bg-transparent outline-none text-base">
            {services.map(s => <option key={s.id} value={s.id}>{lang === 'fr' ? (s.shortNameFr || s.nameFr) : (s.shortNameEn || s.nameEn)}</option>)}
          </select>
        </Field>

        <PriceBox estimate={estimate} lang={lang} />

        {tab === 'oneway' && (
          <RouteMap fromAddr={fromAddr} toAddr={toAddr} lang={lang} />
        )}

        <Button type="submit" className="w-full bg-black hover:bg-black/85 text-white rounded-none h-12 text-sm tracking-[0.2em] uppercase">
          {estimate.kind === 'flat' || estimate.kind === 'distance'
            ? (lang === 'fr' ? 'Réserver' : 'Book now')
            : (lang === 'fr' ? 'Devis' : 'Quote')}
        </Button>
      </form>
    </div>
  );
};

const PriceBox = ({ estimate, lang }) => {
  if (estimate.kind === 'flat') {
    return (
      <div className="bg-black text-white p-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/70 flex items-center gap-1.5">
            <BadgeCheck className="w-3.5 h-3.5" />
            {lang === 'fr' ? 'Forfait aéroport' : 'Airport flat rate'}
          </div>
          <div className="text-xs text-white/70 mt-1">
            {ZONE_LABELS[estimate.zones.from] || ''} ↔ {ZONE_LABELS[estimate.zones.to] || ''} • {estimate.label}
          </div>
        </div>
        <div className="font-serif text-3xl">{estimate.price} €</div>
      </div>
    );
  }
  if (estimate.kind === 'distance') {
    return (
      <div className="bg-[#f6f4f1] p-4 flex items-center justify-between border border-black/10">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-black/60">
            {lang === 'fr' ? 'Tarif estimé' : 'Estimated price'}
          </div>
          <div className="text-xs text-black/60 mt-1">
            ~{estimate.km} km • {estimate.label}{estimate.minApplied ? (lang === 'fr' ? ' • tarif minimum' : ' • minimum fare') : ''}
          </div>
        </div>
        <div className="font-serif text-3xl text-black">{estimate.price} €</div>
      </div>
    );
  }
  // quote
  return null;
};

const Field = ({ icon, label, children }) => (
  <label className="block border-b border-black/15 pb-2">
    <span className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-black/60">{icon}{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export default BookingWidget;
